import { validateQId } from '@exam-management/common/utils';
import {
  ExportSearchResultsSourceFeature,
  type PageInput,
  type SearchQuestionsByQIdsInput,
  useSearchQuestionsByQIdsLazyQuery,
} from '@exam-management/graphql';
import {
  Box,
  Button,
  Divider,
  FileDownloadOutlinedIcon,
  FindInPageOutlinedIcon,
  Flex,
  Loading,
  Paper,
  SearchRoundedIcon,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useSearchByQIDsParams from '../../hooks/use-search-by-qids-params';
import ExportSearchResultsModal from '../export-search-results-modal';
import SearchPagination from '../search-by-keywords/search-pagination';
import SearchResultCard from '../search-by-keywords/search-result-card';
import { useResolvedSearchItems } from '../search-by-keywords/use-resolved-search-items';

import QIdChipInput, { type QIdChipInputHandle } from './qid-chip-input';

const QID_MAX = 300;

export default function SearchByQIDs() {
  const [{ qIds: urlQIds, page, pageSize }, setQueryParams] = useSearchByQIDsParams();

  const [qIds, setQIds] = useState<string[]>(urlQIds);
  const [hasSearched, setHasSearched] = useState(false);
  const [wasCapped, setWasCapped] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [qIdInputKey, setQIdInputKey] = useState(0);
  const [searchedCount, setSearchedCount] = useState(0);

  const [searchQuestions, { data, loading, error }] = useSearchQuestionsByQIdsLazyQuery({
    onCompleted: () => {
      document.getElementById('frame-content')?.scrollTo({ top: 0 });
    },
    onError: () => {
      showSnackbar({
        variant: 'error',
        message: 'ไม่สามารถค้นหาข้อมูลได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
      });
    },
  });

  const lastInput = useRef<SearchQuestionsByQIdsInput | null>(null);
  const chipInputRef = useRef<QIdChipInputHandle>(null);
  const didInit = useRef(false);

  const validQIds = useMemo(() => qIds.filter(validateQId), [qIds]);
  const isOverLimit = useMemo(() => qIds.length > QID_MAX, [qIds]);
  const canSearch = validQIds.length > 0 && !isOverLimit;

  const runSearch = (input: SearchQuestionsByQIdsInput | null, paging: PageInput) => {
    if (!input) return;
    searchQuestions({ variables: { input, paging }, fetchPolicy: 'network-only' });
  };

  const handleSearch = () => {
    const all = chipInputRef.current?.flush() ?? qIds;
    const valid = all.filter(validateQId);
    if (valid.length === 0 || all.length > QID_MAX) return;
    const input = { qIds: valid };
    lastInput.current = input;
    setHasSearched(true);
    setSearchedCount(all.length);
    setQueryParams({ qIds: all, page: 0 });
    runSearch(input, { currentPage: 0, pageSize });
  };

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const valid = urlQIds.filter(validateQId);
    if (valid.length === 0 || urlQIds.length > QID_MAX) return;
    const input = { qIds: valid };
    lastInput.current = input;
    setHasSearched(true);
    setSearchedCount(urlQIds.length);
    runSearch(input, { currentPage: page, pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (newPage: number) => {
    setQueryParams({ page: newPage });
    runSearch(lastInput.current, { currentPage: newPage, pageSize });
  };

  const handleRowsPerPageChange = (newSize: number) => {
    setQueryParams({ pageSize: newSize, page: 0 });
    runSearch(lastInput.current, { currentPage: 0, pageSize: newSize });
  };

  const handleReset = () => {
    setQIds([]);
    setHasSearched(false);
    setWasCapped(false);
    setSearchedCount(0);
    setQueryParams({ qIds: [], page: 0, pageSize: 20 });
    setQIdInputKey((n) => n + 1);
    lastInput.current = null;
  };

  const { items: pageItems, loading: resolvingImages } = useResolvedSearchItems(
    data?.qctSearchQuestionsByQIds.items,
  );
  const total = data?.qctSearchQuestionsByQIds.total ?? 0;
  // Clear notFoundSet during loading so stale results don't mark chips red mid-search
  const notFoundSet = useMemo(
    () => (loading ? new Set<string>() : new Set(data?.qctSearchQuestionsByQIds.notFoundQIds)),
    [data?.qctSearchQuestionsByQIds.notFoundQIds, loading],
  );

  const isInvalid = useCallback(
    (qId: string) => !validateQId(qId) || notFoundSet.has(qId),
    [notFoundSet],
  );

  return (
    <Paper
      sx={(theme) => ({
        width: '100%',
        minHeight: 'calc(100dvh - 181px)',
        borderRadius: theme.shape.rounded(2),
        overflow: 'hidden',
      })}
    >
      <Flex sx={{ flexDirection: 'column' }}>
        <Box
          sx={(theme) => ({
            px: theme.spacing(6),
            py: theme.spacing(5),
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          })}
        >
          <Text variant='h3'>ค้นหาด้วย QID</Text>
        </Box>

        <Flex
          sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6), p: theme.spacing(6) })}
        >
          <Box
            sx={(theme) => ({
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: theme.shape.rounded(2),
              p: theme.spacing(4),
            })}
          >
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
              <Text variant='caption' sx={{ color: isOverLimit ? 'error.main' : 'text.secondary' }}>
                QID: {qIds.length} / {QID_MAX}
              </Text>
              <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1) })}>
                <QIdChipInput
                  key={qIdInputKey}
                  ref={chipInputRef}
                  value={qIds}
                  onChange={setQIds}
                  isInvalid={isInvalid}
                  max={QID_MAX}
                  error={isOverLimit}
                  onCapped={setWasCapped}
                  placeholder='ระบุ QID ที่ต้องการค้นหา'
                />
                {isOverLimit ? (
                  <Text variant='caption' sx={(theme) => ({ color: theme.palette.error.main })}>
                    ค้นหาได้สูงสุด {QID_MAX} QID ต่อครั้ง กรุณาลดจำนวน QID ก่อนค้นหา
                  </Text>
                ) : wasCapped ? (
                  <Text variant='caption' sx={(theme) => ({ color: theme.palette.text.secondary })}>
                    ค้นหาได้สูงสุด {QID_MAX} QID ต่อครั้ง ระบบเพิ่มเฉพาะ {QID_MAX} QID แรก
                  </Text>
                ) : null}
              </Flex>
              <Divider />

              <Flex
                sx={(theme) => ({
                  justifyContent: 'flex-end',
                  gap: theme.spacing(2),
                  height: 36,
                })}
              >
                <Button variant='outlined' color='secondary' onMouseDown={handleReset}>
                  Reset
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<SearchRoundedIcon />}
                  onMouseDown={handleSearch}
                  disabled={!canSearch}
                >
                  ค้นหา
                </Button>
              </Flex>
            </Flex>
          </Box>

          {!hasSearched || (!loading && !!error) ? (
            <Flex
              sx={(theme) => ({
                minHeight: 348,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                gap: theme.spacing(3),
                textAlign: 'center',
              })}
            >
              <SearchRoundedIcon sx={{ color: 'grey.500', fontSize: 96 }} />
              <Text variant='h3'>ค้นหาข้อสอบด้วย QID</Text>
              <Text>ระบุ QID ที่ต้องการแล้วกดค้นหา</Text>
            </Flex>
          ) : null}

          {hasSearched && !loading && !error && total === 0 ? (
            <Flex
              sx={(theme) => ({
                minHeight: 348,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                gap: theme.spacing(3),
                textAlign: 'center',
              })}
            >
              <FindInPageOutlinedIcon sx={{ color: 'grey.400', fontSize: 96 }} />
              <Text variant='h3'>ไม่พบข้อสอบที่ตรงกับ QID ที่ค้นหา</Text>
              <Text>ลองเปลี่ยน QID ที่ใช้ค้นหา</Text>
            </Flex>
          ) : null}

          {hasSearched && !loading && !resolvingImages && !error && total > 0 ? (
            <>
              <Flex
                sx={(theme) => ({
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='h3'>
                  พบ {total} จาก {searchedCount} รายการ
                </Text>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={() => setIsExportModalOpen(true)}
                >
                  Export หน้านี้ ({pageItems.length} รายการ)
                </Button>
              </Flex>

              <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
                {pageItems.map((item) => (
                  <SearchResultCard key={item.id} item={item} />
                ))}
              </Flex>

              <SearchPagination
                total={total}
                page={page}
                rowsPerPage={pageSize}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </>
          ) : null}

          {hasSearched && (loading || resolvingImages) ? (
            <Flex
              sx={(theme) => ({
                minHeight: 348,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                gap: theme.spacing(3),
              })}
            >
              <Loading />
            </Flex>
          ) : null}
        </Flex>
      </Flex>

      <ExportSearchResultsModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        questionIds={pageItems.map((item) => item.id)}
        sourceFeature={ExportSearchResultsSourceFeature.SearchByQid}
        total={pageItems.length}
        page={page}
        pageSize={pageSize}
      />
    </Paper>
  );
}
